import React, { useState } from "react";
import { Bell, X, MessageCircle, Heart, Sparkles, Clock } from "lucide-react";
import { useNotifications } from "@/contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const NotificationBell = () => {
  const navigate = useNavigate();
  const { 
    totalUnread, 
    unreadMessages, 
    newMatches,
    notifications,
    markAllAsRead,
    clearNotification 
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      markAllAsRead();
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-6 h-6 text-white" />
        {totalUnread > 0 && (
          <span 
            className="absolute top-0 right-0 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1"
            data-testid="bell-badge"
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl z-50 overflow-hidden"
              data-testid="notification-panel"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] px-4 py-3 text-white">
                <h3 className="font-semibold">Notifications</h3>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 border-b">
                <button
                  onClick={() => { navigate("/messages"); setIsOpen(false); }}
                  className="flex items-center gap-2 p-2 rounded-xl hover:bg-white transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">{unreadMessages}</p>
                    <p className="text-xs text-gray-500">Messages</p>
                  </div>
                </button>
                <button
                  onClick={() => { navigate("/matches"); setIsOpen(false); }}
                  className="flex items-center gap-2 p-2 rounded-xl hover:bg-white transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">{newMatches}</p>
                    <p className="text-xs text-gray-500">New Matches</p>
                  </div>
                </button>
              </div>

              {/* Notification List */}
              <div className="max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No notifications yet</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
                        !notif.read ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notif.type === "message" ? "bg-blue-100" :
                          notif.type === "match" ? "bg-green-100" :
                          "bg-gray-100"
                        }`}>
                          {notif.type === "message" ? (
                            <MessageCircle className="w-4 h-4 text-blue-600" />
                          ) : notif.type === "match" ? (
                            <Heart className="w-4 h-4 text-green-600" />
                          ) : (
                            <Bell className="w-4 h-4 text-gray-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 line-clamp-2">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(notif.timestamp)}
                          </p>
                        </div>
                        <button
                          onClick={() => clearNotification(notif.id)}
                          className="p-1 hover:bg-gray-200 rounded-full"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="p-2 border-t bg-gray-50">
                  <button
                    onClick={() => { navigate("/messages"); setIsOpen(false); }}
                    className="w-full text-center text-sm text-[#1C3A7A] font-medium py-2 hover:underline"
                  >
                    View All Messages
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
